import Config from '@config';
import { normalizeInfiniteQueryData } from '@gasbi/shared/common/features/utils';
import { createApi } from '@reduxjs/toolkit/query/react';

import { CACHE_EXPIRATION_TIME_REFERENCE_BOOK } from '@/features/constants';
import { FiltersGrid, Pagination } from '@/features/types';
import { infiniteQueryOptions } from '@/features/utils';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

import { DeviceManufacturer, DeviceManufacturerRequestBody } from './types';

export const meteringDeviceManufacturersAPI = createApi({
  reducerPath: 'meteringDeviceManufacturersAPI',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/device-manufacturer`,
  }),
  tagTypes: ['DeviceManufacturer', 'DeviceManufacturers'],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME_REFERENCE_BOOK,
  endpoints: builder => ({
    getDeviceManufacturers: builder.query<DeviceManufacturer[], void>({
      query: () => ({
        url: '/',
      }),
      providesTags: () => ['DeviceManufacturer', 'DeviceManufacturers'],
    }),
    getDeviceManufacturersI: builder.infiniteQuery<
      DeviceManufacturer[],
      FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/query`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['DeviceManufacturer', 'DeviceManufacturers'],
    }),
    getDeviceManufacturerById: builder.query<DeviceManufacturer, string>({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: () => ['DeviceManufacturer'],
    }),
    createDeviceManufacturer: builder.mutation<
      DeviceManufacturer,
      DeviceManufacturerRequestBody
    >({
      query: data => ({
        url: '/',
        method: 'POST',
        data,
      }),
      invalidatesTags: result => [
        'DeviceManufacturer',
        { type: 'DeviceManufacturer', id: result?.id },
      ],
    }),
    updateDeviceManufacturer: builder.mutation<
      DeviceManufacturer,
      DeviceManufacturerRequestBody
    >({
      query: data => ({
        url: `/${data.id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: result => [
        'DeviceManufacturer',
        { type: 'DeviceManufacturer', id: result?.id },
      ],
    }),
    deleteDeviceManufacturerById: builder.mutation<void, string>({
      query: id => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, id) => [
        'DeviceManufacturer',
        { type: 'DeviceManufacturer', id },
      ],
    }),
  }),
});

export const {
  useGetDeviceManufacturersQuery,
  useCreateDeviceManufacturerMutation,
  useUpdateDeviceManufacturerMutation,
} = meteringDeviceManufacturersAPI;

export const useGetDeviceManufacturersIInfiniteQuery =
  normalizeInfiniteQueryData(
    meteringDeviceManufacturersAPI.useGetDeviceManufacturersIInfiniteQuery,
  );
